import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Refresh,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';
import apiService from '../services/api';
import { Member } from '../types';

const Members: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{show: boolean, message: string, type: 'success' | 'error' | 'info'}>({
    show: false,
    message: '',
    type: 'info'
  });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Search debounce
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const shouldRefocusSearchRef = useRef(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { socket, isConnected } = useSocket();
  
  // Check for URL parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    const typeParam = params.get('type');
    const filterParam = params.get('filter'); // Legacy support

    if (statusParam) {
      setStatusFilter(statusParam);
    } else if (typeParam) {
      setStatusFilter(typeParam);
    } else if (filterParam) {
      setStatusFilter(filterParam);
    }
  }, [location.search]);
  
  // Track component mount state to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // AbortController for API requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Reset to first page when searching
    }, 500); // 500ms delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Fetch members data with pagination and search
  const fetchMembersData = useCallback(async () => {
    if (!isMountedRef.current) {
      console.log('[Members] Component unmounted, skipping fetchMembers');
      return;
    }
    
    console.log('[Members] Fetching members data');
    
    try {
      setLoading(true);
      setError(null);
      
      // Ensure we have a valid abort controller
      if (!abortControllerRef.current) {
        abortControllerRef.current = new AbortController();
      }
      
      // Prepare query parameters for server-side pagination and search
      const params: any = {
        page: page + 1, // Server expects 1-based page numbers
        limit: rowsPerPage,
      };

      if (statusFilter) {
        // Check if it's a membership type filter or status filter
        if (statusFilter === 'Outside Member' || statusFilter === 'Estate Members') {
          params.type = statusFilter;
        } else {
          params.status = statusFilter;
        }
      }

      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }
      
      // Make API request with abort signal
      const response = await apiService.getMembers(params, abortControllerRef.current.signal);
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        console.log('[Members] Got members data:', response.data?.length || 0, 'members');
        console.log('[Members] Pagination info:', response.pagination);
        setMembers(response.data || []);
        setTotalCount(response.pagination?.total || 0);
        setLoading(false);
      }
    } catch (error: any) {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        // Don't treat aborted requests as errors
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
          console.log('[Members] Request was canceled or aborted');
          return;
        }
        
        console.error('[Members] Failed to fetch members:', error);
        setError(error.response?.data?.message || 'Failed to fetch members');
        setLoading(false);
      }
    }
  }, [page, rowsPerPage, statusFilter, debouncedSearchTerm]);

  // Initial data fetch
  useEffect(() => {
    console.log('[Members] Component mounted or dependencies changed');
    
    // Set mounted flag
    isMountedRef.current = true;
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    // Fetch data
    fetchMembersData();
    
    // Cleanup function
    return () => {
      console.log('[Members] Component unmounting, aborting requests');
      isMountedRef.current = false;
      
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [fetchMembersData]);

  // Socket event listeners
  useEffect(() => {
    if (socket) {
      console.log('[Members] Setting up socket event listeners');
      
      // Handler for new member added
      const handleMemberCreated = (data: { member: Member }) => {
        console.log('[Members] Socket event: member:created', data);
        if (isMountedRef.current) {
          setMembers(prev => [data.member, ...prev]);
          showNotification(`Member ${data.member.firstName} ${data.member.lastName} was added`, 'success');
        }
      };
      
      // Handler for member updated
      const handleMemberUpdated = (data: { member: Member }) => {
        console.log('[Members] Socket event: member:updated', data);
        if (isMountedRef.current) {
          setMembers(prev => prev.map(member => 
            member._id === data.member._id ? data.member : member
          ));
          showNotification(`Member ${data.member.firstName} ${data.member.lastName} was updated`, 'info');
        }
      };
      
      // Handler for member deleted
      const handleMemberDeleted = (data: { memberId: string }) => {
        console.log('[Members] Socket event: member:deleted', data);
        if (isMountedRef.current) {
          setMembers(prev => {
            const memberToRemove = prev.find(m => m._id === data.memberId);
            const newMembers = prev.filter(member => member._id !== data.memberId);
            
            if (memberToRemove) {
              showNotification(`Member ${memberToRemove.firstName} ${memberToRemove.lastName} was deleted`, 'info');
            }
            
            return newMembers;
          });
        }
      };

      // Register socket event handlers
      socket.on('member:created', handleMemberCreated);
      socket.on('member:updated', handleMemberUpdated);
      socket.on('member:deleted', handleMemberDeleted);

      // Cleanup function
      return () => {
        console.log('[Members] Removing socket event listeners');
        socket.off('member:created', handleMemberCreated);
        socket.off('member:updated', handleMemberUpdated);
        socket.off('member:deleted', handleMemberDeleted);
      };
    }
    
    // If socket is not available, log it
    if (!isConnected) {
      console.log('[Members] Socket is not connected');
    }
  }, [socket, isConnected]);

  // Helper to show notifications
  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    if (isMountedRef.current) {
      setNotification({
        show: true,
        message,
        type
      });
    }
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({...prev, show: false}));
  };

  // Handle member deletion
  const handleDeleteMember = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this member?')) {
      try {
        await apiService.deleteMember(id);
        // Filter out the deleted member from the local state immediately
        setMembers(prevMembers => prevMembers.filter(member => member._id !== id));
        showNotification('Member deleted successfully', 'success');
      } catch (error: any) {
        console.error('[Members] Failed to delete member:', error);
        showNotification(error.response?.data?.message || 'Failed to delete member', 'error');
      }
    }
  };

  // Determine status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'suspended':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  // Manual refresh handler
  const handleRefresh = () => {
    console.log('[Members] Manual refresh requested');
    fetchMembersData();
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: string | null) => {
    console.log('[Members] Status filter changed to:', status);
    shouldRefocusSearchRef.current = searchTerm.trim().length > 0;
    setStatusFilter(status);
    setPage(0); // Reset to first page when filtering
  };

  // Handle pagination change
  const handlePageChange = (event: unknown, newPage: number) => {
    shouldRefocusSearchRef.current = searchTerm.trim().length > 0;
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    shouldRefocusSearchRef.current = searchTerm.trim().length > 0;
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  useEffect(() => {
    if (!loading && shouldRefocusSearchRef.current && searchTerm.trim().length > 0) {
      if (document.activeElement !== searchInputRef.current) {
        searchInputRef.current?.focus();
      }
      shouldRefocusSearchRef.current = false;
    }
  }, [loading, searchTerm]);

  // Main render
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box /> {/* Empty box to maintain layout */}
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/members/add')}
          >
            Add Member
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search members..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            onClick={handleRefresh}
            variant="contained"
            startIcon={<Refresh />}
          >
            Retry
          </Button>
        </Box>
      )}

      {/* Status filter buttons */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ width: '100%', mb: 1 }}>
            Filter by Status:
          </Typography>
          <Button
            variant={statusFilter === null ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleStatusFilterChange(null)}
          >
            All
          </Button>
          <Button
            variant={statusFilter === 'active' ? 'contained' : 'outlined'}
            color="success"
            size="small"
            onClick={() => handleStatusFilterChange('active')}
          >
            Active
          </Button>
          <Button
            variant={statusFilter === 'inactive' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => handleStatusFilterChange('inactive')}
          >
            Inactive
          </Button>
          <Button
            variant={statusFilter === 'suspended' ? 'contained' : 'outlined'}
            color="warning"
            size="small"
            onClick={() => handleStatusFilterChange('suspended')}
          >
            Suspended
          </Button>
          <Button
            variant={statusFilter === 'expired' ? 'contained' : 'outlined'}
            color="error"
            size="small"
            onClick={() => handleStatusFilterChange('expired')}
          >
            Expired
          </Button>
          <Button
            variant={statusFilter === 'expiring-soon' ? 'contained' : 'outlined'}
            color="warning"
            size="small"
            onClick={() => handleStatusFilterChange('expiring-soon')}
          >
            Expiring Soon
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ width: '100%', mb: 1 }}>
            Filter by Membership Type:
          </Typography>
          <Button
            variant={statusFilter === 'Outside Member' ? 'contained' : 'outlined'}
            color="success"
            size="small"
            onClick={() => handleStatusFilterChange('Outside Member')}
          >
            Outside Members
          </Button>
          <Button
            variant={statusFilter === 'Estate Members' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => handleStatusFilterChange('Estate Members')}
          >
            Estate Members (Home Owners & Tenants)
          </Button>
        </Box>
      </Paper>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          <em>Click on a row to view member details</em>
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Membership Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Join Date</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box display="flex" alignItems="center" justifyContent="center" py={3}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>Loading members...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {members.map((member) => (
              <TableRow 
                key={member._id}
                hover
                onClick={() => navigate(`/members/${member._id}`)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)', 
                  }
                }}
              >
                <TableCell>
                  <Typography variant="body1">
                    {member.fullName || `${member.firstName} ${member.lastName}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  {typeof member.phone === 'string'
                    ? member.phone
                    : member.phone?.mobile || ''}
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.membershipType}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.membershipStatus}
                    color={getStatusColor(member.membershipStatus) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {new Date(member.startDate).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {member.endDate ? new Date(member.endDate).toLocaleDateString() : 'N/A'}
                    {(() => {
                      if (!member.endDate) return null;
                      
                      const expiryDate = new Date(member.endDate);
                      const today = new Date();
                      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
                      
                      if (daysUntilExpiry < 0) {
                        return <Chip label="Expired" color="error" size="small" />;
                      } else if (daysUntilExpiry <= 30) {
                        return <Chip label={`${daysUntilExpiry} days left`} color="warning" size="small" />;
                      }
                      return null;
                    })()}
                  </Box>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Edit Member">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/members/${member._id}/edit`);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Member">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMember(member._id);
                      }}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalCount > 0 && (
        <Paper sx={{ mt: 2 }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
            labelRowsPerPage="Members per page:"
          />
        </Paper>
      )}

      {members.length === 0 && debouncedSearchTerm && !loading && (
        <Box textAlign="center" mt={4}>
          <Typography color="textSecondary">
            No members found matching "{debouncedSearchTerm}"
          </Typography>
        </Box>
      )}

      {members.length === 0 && !loading && !debouncedSearchTerm && totalCount === 0 && (
        <Box textAlign="center" mt={4}>
          <Typography color="textSecondary" gutterBottom>
            No members found
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/members/add')}
          >
            Add Your First Member
          </Button>
        </Box>
      )}

      {/* Notification snackbar */}
      <Snackbar 
        open={notification.show} 
        autoHideDuration={5000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.type} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Members;
